import { modalToText } from "@/common/dict"
import * as AdminService from "@/services/admin/property-set"
import { OrganizationPropertySetService } from "@/services/admin/property-set"
import { ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { useRequest } from "ahooks"
import { Breadcrumb, Button, Input, Modal, Row, Space, Table } from "antd"
import classNames from "classnames"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { EditJsonModal } from "./EditJsonModal"

interface PropertySetsProps {
  orgId?: number
}

export function PropertySets(props: PropertySetsProps) {
  const { orgId } = props
  const navigate = useNavigate()
  
  const service = useMemo(() => {
    if (orgId) {
      return {
        getPropertySets: () => OrganizationPropertySetService.getPropertySets(orgId),
        removePropertySet: (id) => OrganizationPropertySetService.removePropertySet(id),
        duplicatePropertySet: (id) => OrganizationPropertySetService.duplicatePropertySet(orgId, id),
        generateSetJson: (params) => OrganizationPropertySetService.generateSetJson(orgId, params),
        getPropertySet: (id) => OrganizationPropertySetService.getPropertySet(id),
      }
    }
    return AdminService
  }, [orgId])

  const { data = [], loading, refresh } = useRequest(service.getPropertySets, {
    refreshDeps: [service]
  })
  const [searchText, setSearchText] = useState('')

  const filteredData = useMemo(() => {
    if (!searchText) {
      return data
    }

    return data.filter((item) => {
      return item.name.toLowerCase().includes(searchText.toLowerCase())
    })
  }, [data, searchText])

  const duplicate = async (id: number) => {
    await service.duplicatePropertySet(id)
    refresh()
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Thermodynamic Property Model',
      dataIndex: 'fugModel',
      key: 'model',
      render: (model) => {
        return modalToText(model)
      }
    },
    {
      title: 'Components',
      dataIndex: 'components',
      key: 'components',
      render: (components) => {
        return components.map(item => item.name).join(',')
      }
    },
    {
      title: 'Property Set Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, item) => {
        return <Space>
          

          <Button size="small" onClick={() => {
            navigate(`${item.id}/basic`)
          }}>Edit</Button>

          <Button size="small" onClick={() => duplicate(item.id)}>Duplicate</Button>
          
          <Button size="small" danger onClick={() => {
            Modal.confirm({
              icon: <ExclamationCircleOutlined />,
              content: 'Are you sure to delete?',
              onOk: async () => {
                try {
                  await service.removePropertySet(item.id);
                  refresh()
                  return true
                } catch (error) {
                  return false
                }
              }
            })
          }}>Delete</Button>
        </Space>
      }
    },
    {
      title: 'Simulation File Actions',
      key: 'simulation',
      render: (_, record) => {
        return <Space>
          <Button size="small" disabled={!!record.json} onClick={async () => {
            await service.generateSetJson({setId: record.id})
            refresh()
          }}>Generate</Button>

          <Button size="small" onClick={async () => {
            const propertySet = await service.getPropertySet(record.id)
            EditJsonModal.show({propertySet, onSuccess: () => refresh(), orgId})
          }}>Edit</Button>

          <Button size="small" danger onClick={() => {
          }}>Delete</Button>
        </Space>
      }
    },
  ]



  return <div>
    <Breadcrumb>
      <Breadcrumb.Item>Property Sets</Breadcrumb.Item>
    </Breadcrumb>
    <Row justify={'space-between'}>
      <Input
        placeholder='Search by Name'
        style={{ width: 300 }}
        allowClear
        onChange={(event) => {
          setSearchText(event.target.value)
        }}
      />
      <Button type='primary' icon={<PlusOutlined />} onClick={() => {
        navigate('create/basic')
      }} >
        Create
      </Button>
    </Row>
    <div className={classNames('mt-2')}>
        <Table
          bordered
          pagination={{ pageSize: 10 }}
          size='small'
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
        />
      </div>
  </div>
}
